/**
 *  @class
 *  @function FacetsToggle
 */
class FacetsToggle {
  constructor() {
    this.container = document.getElementById('Facet-Drawer');
    const button = document.getElementById('Facets-Toggle');

    // Add functionality to buttons (nur einmal pro Button-Element - bei
    // 0-Treffer-Renders bleibt der Button bestehen und wird erneut initialisiert)
    if (button && !button.dataset.toggleBound) {
      button.dataset.toggleBound = 'true';
      button.addEventListener('click', (e) => {
        e.preventDefault();
        const opening = !this.container.classList.contains('active');
        if (opening && window.ThemeFocusTrap) ThemeFocusTrap.capture(this.container);
        document.body.classList.toggle('open-cc');
        this.container.classList.toggle('active');
        this.container.removeAttribute('inert');
        if (opening) {
          this.container.querySelector('.side-panel-close')?.focus();
        } else if (window.ThemeFocusTrap) {
          ThemeFocusTrap.restore(this.container);
        }
      });
    }
  }
}

/**
 *  @class
 *  @function SidebarToggle
 */
class SidebarToggle {
  constructor() {
    this.container = document.getElementById('Sidebar-Container');
    const button = document.getElementById('Sidebar-Toggle');

    // Add functionality to buttons (nur einmal pro Button-Element, s. FacetsToggle)
    if (button && !button.dataset.toggleBound) {
      button.dataset.toggleBound = 'true';
      const span = button.querySelector('span');

      button.addEventListener('click', (e) => {
        e.preventDefault();
        this.container.classList.toggle('facets--sidebar-open');

        span.textContent = this.container.classList.contains('facets--sidebar-open') ? theme.strings.hideFilters : theme.strings.showFilters;
      });
    }
  }
}

/**
 *  @class
 *  @function FacetFiltersForm
 */
class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    this.querySelectorAll('.thb-filter-content--button').forEach((more_button) => {
      more_button.addEventListener('click', this.onMore);
    });
    const form = this.querySelector('form');
    // Abort an in-flight request the moment the user touches a filter again, so
    // a slow response from a previous click can't rebuild the sidebar and wipe
    // a checkbox that was just toggled (which made the new check only appear
    // after the reload finished).
    form.addEventListener('input', () => {
      if (FacetFiltersForm.abortController) FacetFiltersForm.abortController.abort();
    });
    form.addEventListener('input', this.debouncedOnSubmit.bind(this));
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const container = document.querySelectorAll('.thb-filter-count, .mobile-filter-count');
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');

    for (var item of container) {
      item.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = element => element.url === url;

      if (FacetFiltersForm.filterData.some(filterDataUrl)) {
        FacetFiltersForm.renderSectionFromCache(filterDataUrl, event);
      } else {
        FacetFiltersForm.renderSectionFromFetch(url, event);
      }
    });
    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);

  }

  static renderSectionFromFetch(url, event) {
    FacetFiltersForm.abortController = new AbortController();
    fetch(url, { signal: FacetFiltersForm.abortController.signal })
      .then(response => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, {
          html,
          url
        }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        FacetFiltersForm.publishUpdateEvent();

        new FacetsToggle();
        new SidebarToggle();
      })
      .catch((error) => {
        // Ignore aborts (superseded by a newer request); rethrow real errors.
        if (error.name !== 'AbortError') throw error;
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    FacetFiltersForm.publishUpdateEvent();

    new FacetsToggle();
    new SidebarToggle();
  }

  static publishUpdateEvent() {
    // typeof-Guard: facets.js laeuft (defer) VOR app.js, publishStandardEvent
    // existiert hier erst zur Interaktionszeit — und app.js koennte fehlschlagen.
    const SE = window.Shopify && window.Shopify.StandardEvents;
    if (!SE || typeof publishStandardEvent !== 'function') return;
    try {
      const searchParams = new URLSearchParams(FacetFiltersForm.searchParamsPrev);
      if (theme.settings.collection_id) {
        publishStandardEvent('CollectionUpdateEvent', {
          collection: { id: theme.settings.collection_id },
          productFilters: SE.CollectionUpdateEvent.parseProductFilters(searchParams),
          sortKey: SE.CollectionUpdateEvent.getSortKey(searchParams)
        });
      } else if (theme.routes.search_url && window.location.pathname.indexOf(theme.routes.search_url) !== -1) {
        // Greift nur bei nativer Shopify-Suche; mit search_engine=typesense laedt
        // main-search.liquid facets.js nicht (SearchUpdate kommt dann ggf. aus typesense-search-page.js).
        publishStandardEvent('SearchUpdateEvent', {
          searchTerm: searchParams.get('q') || '',
          productFilters: SE.SearchUpdateEvent.parseProductFilters(searchParams),
          sortKey: SE.SearchUpdateEvent.getSortKey(searchParams)
        });
      }
    } catch (e) {
      console.error('Standard event failed: collection/search update', e);
    }
  }

  static renderProductGridContainer(html) {
    const parsedDocument = new DOMParser().parseFromString(html, 'text/html');

    // 0-Treffer-Response: nur den Produktbereich tauschen, Bar + Sidebar samt
    // Filter-Zustand behalten (die Response enthaelt keine Facet-Daten, ein
    // Voll-Replace wuerde alle gesetzten Checkboxen verwerfen).
    if (parsedDocument.querySelector('#product-grid.collection-empty')) {
      const productsTarget = document.querySelector('#ProductGridContainer .sidebar-container--products');
      const productsSource = parsedDocument.querySelector('#ProductGridContainer .sidebar-container--products');
      if (productsTarget && productsSource) {
        productsTarget.innerHTML = productsSource.innerHTML;
        document.querySelectorAll('.thb-filter-count, .mobile-filter-count').forEach((element) => {
          element.classList.remove('loading');
        });
        FacetFiltersForm.toggleActiveFacets(false);
        return;
      }
    }

    // The sidebar lives inside #ProductGridContainer, but its open/closed state
    // is client-side only - carry it across the innerHTML replace, otherwise
    // the sidebar snaps shut after every filter click.
    const sidebar = document.getElementById('Sidebar-Container');
    const sidebarOpen = sidebar ? sidebar.classList.contains('facets--sidebar-open') : false;

    document.getElementById('ProductGridContainer').innerHTML = parsedDocument.getElementById('ProductGridContainer').innerHTML;

    if (sidebarOpen) {
      const newSidebar = document.getElementById('Sidebar-Container');
      if (newSidebar) newSidebar.classList.add('facets--sidebar-open');
      const toggleSpan = document.querySelector('#Sidebar-Toggle span');
      if (toggleSpan) toggleSpan.textContent = theme.strings.hideFilters;
    }

    let top = document.getElementById('ProductGridContainer').getBoundingClientRect().top + document.documentElement.scrollTop - parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height'), 10) - 30;

    document.getElementById('ProductGridContainer').querySelectorAll('.lazyload').forEach((image) => {
      lazySizes.loader.unveil(image);
    });

    window.scrollTo({
      top: top,
      left: 0,
      behavior: "smooth"
    });
  }

  static renderProductCount(html) {
    const countHtml = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount');
    const container = document.getElementsByClassName('thb-filter-count');

    if (countHtml) {
      for (var item of container) {
        item.innerHTML = countHtml.innerHTML;
        item.classList.remove('loading');
      }
    }

  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    // 0-Treffer-Response: Shopify rendert dort keine Facet-Daten (keine active-
    // Flags, Listen-Gruppen fehlen komplett). Die Formulare deshalb unangetastet
    // lassen - Checkboxen/Chips behalten ihren Zustand und der Nutzer kann die
    // Kombination direkt korrigieren, ohne dass Filter still verloren gehen.
    if (parsedHTML.querySelector('#product-grid.collection-empty')) return;
    // Desktop and mobile rows share data-index values, so every replacement has
    // to stay inside its own form. A document-wide lookup hits the mobile drawer
    // first (it precedes the grid container in the DOM) and plants desktop
    // markup/ids there - duplicate ids then make desktop labels toggle the
    // hidden mobile inputs, whose stale form drops previously selected filters.
    const clickedRow = event ? event.target.closest('collapsible-row') : undefined;
    let countsToRender = null;

    ['#FacetFiltersForm', '#FacetFiltersFormMobile'].forEach((formId) => {
      const liveForm = document.querySelector(formId);
      if (!liveForm) return;
      parsedHTML.querySelectorAll(`${formId} collapsible-row`).forEach((element) => {
        const target = liveForm.querySelector(`collapsible-row[data-index="${element.dataset.index}"]`);
        if (!target) return;
        if (clickedRow && target === clickedRow) {
          countsToRender = element;
          return;
        }
        target.innerHTML = element.innerHTML;
      });
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);
    new FacetsToggle();
    new SidebarToggle();

    if (countsToRender) FacetFiltersForm.renderCounts(countsToRender, clickedRow);
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelectorAll(selector).forEach((element) => {
        element.innerHTML = activeFacetsElement.innerHTML;
      });
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-filter-count'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelectorAll(selector).forEach((element) => {
        element.innerHTML = html.querySelector(selector).innerHTML;
        element.classList.remove('loading');
      });
    });

  }

  static renderCounts(source, target) {
    const targetElement = target.querySelector('.facets__selected');
    const sourceElement = source.querySelector('.facets__selected');

    if (sourceElement && targetElement) {
      target.querySelector('.facets__selected').outerHTML = source.querySelector('.facets__selected').outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({
      searchParams
    }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      }];
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const formData = new FormData(event.target.closest('form'));
    const searchParams = new URLSearchParams(formData);
    if (searchParams.get('filter.v.price.gte') === "0.00") {
      searchParams.delete('filter.v.price.gte');
    }
    if (document.querySelector('.price_slider')) {
      if (searchParams.get('filter.v.price.lte') === parseFloat(document.querySelector('.price_slider').dataset.max).toFixed(2)) {
        searchParams.delete('filter.v.price.lte');
      }
    }
    FacetFiltersForm.renderPage(searchParams.toString(), event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url = event.currentTarget.href.indexOf('?') == -1 ? '' : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }

  onMore(e) {
    let ul = this.closest('.thb-filter-content').querySelector('ul');

    if (ul.hasAttribute('filters-open')) {
      this.textContent = theme.strings.showMore;
      ul.removeAttribute('filters-open');
    } else {
      this.textContent = theme.strings.showLess;
      ul.setAttribute('filters-open', '');
    }
    e.preventDefault();
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.abortController = null;
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

/**
 *  @class
 *  @function FacetRemove
 */
class FacetRemove extends HTMLElement {
  constructor() {
    super();
    // Delegation statt Listener pro <a>: das Drawer-Element ueberlebt AJAX-Renders,
    // seine Anchors werden aber per innerHTML ersetzt und verloeren ihre Listener
    // (Chip-Klick loeste dann einen vollen Page-Load aus).
    this.addEventListener('click', (event) => {
      const link = event.target.closest('a');
      if (!link || !this.contains(link)) return;
      event.preventDefault();
      const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
      form.onActiveFilterClick({
        preventDefault: () => {},
        currentTarget: link
      });
    });
  }
}

customElements.define('facet-remove', FacetRemove);


/**
 *  @class
 *  @function FacetToolbar
 */
class FacetToolbar extends HTMLElement {
  constructor() {
    super();
  }
  connectedCallback() {
    this.productGrid = this.closest('#ProductGridContainer');
    this.compareToggle = this.querySelector('#compare_toggle');
    this.layoutSwitcher = this.querySelector('.facets--bar-view-as');
    this.buttons = this.querySelectorAll('.facets--bar-view-as-button');
    this.productLayout = window.localStorage.getItem('product-layout');
    if (this.layoutSwitcher) {
      this.buttons.forEach((button) => {
        button.addEventListener('click', (evt) => {
          [].forEach.call(this.buttons, function (el) {
            el.removeAttribute('disabled');
          });
          this.onLayoutChange(button, button.dataset.value);
          evt.preventDefault();
        });
      });

      if (this.productLayout && this.productLayout === 'list') {
        const toggle = this.querySelector(`.facets--bar-view-as-button-${this.productLayout}`);
        if (toggle) toggle.click();
      }
    }
    if (this.compareToggle) {
      this.compareToggle.addEventListener('change', this.onCompare);
    }
  }
  onLayoutChange(button, value) {
    button.setAttribute('disabled', '');
    this.productGrid.dataset.layout = value;
    window.localStorage.setItem('product-layout', value);
  }
  onCompare(e) {
    document.body.classList.toggle('compare-true');
    e.preventDefault();
  }
}
customElements.define('facet-toolbar', FacetToolbar);

/**
 *  @class
 *  @function PriceSlider
 */
class PriceSlider extends HTMLElement {

  constructor() {
    super();
  }
  connectedCallback() {
    this.slider = this.querySelector('.price_slider');
    this.amounts = this.querySelector('.price_slider_amount');
    this.form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    this.inputEvent = new CustomEvent('input');

    const args = {
      start: [
        parseFloat(this.slider.dataset.minValue || 0),
        parseFloat(this.slider.dataset.maxValue || this.slider.dataset.max)
      ],
      connect: true,
      step: 10,
      direction: document.dir,
      handleAttributes: [
        { 'aria-label': 'lower' },
        { 'aria-label': 'upper' },
      ],
      range: {
        'min': 0,
        'max': parseFloat(this.slider.dataset.max)
      }
    };
    noUiSlider.create(this.slider, args);

    this.slider.noUiSlider.on('update', this.onSliderUpdate.bind(this));
    this.slider.noUiSlider.on('change', this.onSliderChange.bind(this));

  }
  onSliderUpdate(values) {
    this.amounts.querySelector('.field__input_min').value = values[0];
    this.amounts.querySelector('.field__input_max').value = values[1];
  }

  onSliderChange(values) {
    this.form.querySelector('form').dispatchEvent(this.inputEvent);
  }
}
customElements.define('price-slider', PriceSlider);

window.addEventListener('load', () => {
  new FacetsToggle();
  new SidebarToggle();
});